import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, Link, useLocation } from 'react-router-dom'; // Import Outlet, Link, and useLocation
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import {
  AppBar, Toolbar, IconButton, Typography, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, Box, CssBaseline, Tooltip, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InventoryIcon from '@mui/icons-material/Inventory';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ConstructionIcon from '@mui/icons-material/Construction';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PeopleIcon from '@mui/icons-material/People'; // For user management
import ApprovalIcon from '@mui/icons-material/Approval'; // For approvals
import AssessmentIcon from '@mui/icons-material/Assessment'; // For reports
import ScienceIcon from '@mui/icons-material/Science'; // For Bahan/Alat Gelas
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'; // For Monitoring
import ReportProblemIcon from '@mui/icons-material/ReportProblem'; // For Laporan Kerusakan
import ActiveUsageScreen from '../mahasiswa/ActiveUsageScreen';
import MahasiswaDashboardHome from '../mahasiswa/MahasiswaDashboardHome';
import AdminDashboardHome from '../admin/AdminDashboardHome';
import DosenDashboardHome from '../dosen/DosenDashboardHome';
import LaboranDashboardHome from '../laboran/LaboranDashboardHome';

const drawerWidth = 240;

// Role-based Dashboard Home Wrapper
const RoleBasedDashboardHome = ({ userRole, children }) => {
  const location = useLocation();
  // Check if we're on the index route (dashboard home)
  const isDashboardHome = location.pathname === '/dashboard' || location.pathname === '/dashboard/';

  if (isDashboardHome) {
    // Show role-specific dashboard home
    if (userRole === 'admin') {
      return <AdminDashboardHome />;
    } else if (userRole === 'mahasiswa') {
      return <MahasiswaDashboardHome />;
    } else if (userRole === 'dosen') {
      return <DosenDashboardHome />;
    } else if (userRole === 'laboran') {
      return <LaboranDashboardHome />;
    }
    // Default fallback
    return <MahasiswaDashboardHome />;
  }

  // Not on dashboard home, render children (nested routes)
  return <>{children}</>;
};

// --- Define Menu Items for Each Role ---

const mahasiswaMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' }, // Link to base dashboard path
  { text: 'Katalog Instrumen', icon: <InventoryIcon />, path: 'katalog-instrumen' },
  { text: 'Katalog Alat Gelas', icon: <ScienceIcon />, path: 'katalog-alat-gelas' },
  { text: 'Katalog Bahan', icon: <ScienceIcon />, path: 'katalog-bahan' },
  { text: 'Jadwal Penggunaan', icon: <CalendarTodayIcon />, path: 'jadwal-penggunaan' },
  { text: 'Form Peminjaman Alat', icon: <EditNoteIcon />, path: 'form-peminjaman-alat' },
  { text: 'Form Permintaan Bahan', icon: <EditNoteIcon />, path: 'form-permintaan-bahan' },
  { text: 'Status Permintaan', icon: <PlaylistAddCheckIcon />, path: 'status-permintaan' },
  { text: 'Status Penggunaan Alat', icon: <ConstructionIcon />, path: 'alat-siap-digunakan' }, // Renamed
  { text: 'Scan Alat', icon: <QrCodeScannerIcon />, path: '/scan-alat' }, // Use absolute path for top-level route
  { text: 'Profil', icon: <AccountCircleIcon />, path: 'edit-profil' },
];

const dosenMenuItems = [
  // Common with Mahasiswa
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Katalog Instrumen', icon: <InventoryIcon />, path: 'katalog-instrumen' },
  { text: 'Katalog Alat Gelas', icon: <ScienceIcon />, path: 'katalog-alat-gelas' },
  { text: 'Katalog Bahan', icon: <ScienceIcon />, path: 'katalog-bahan' },
  { text: 'Jadwal Penggunaan', icon: <CalendarTodayIcon />, path: 'jadwal-penggunaan' },
  { text: 'Form Peminjaman Alat', icon: <EditNoteIcon />, path: 'form-peminjaman-alat' },
  { text: 'Form Permintaan Bahan', icon: <EditNoteIcon />, path: 'form-permintaan-bahan' },
  { text: 'Status Permintaan', icon: <PlaylistAddCheckIcon />, path: 'status-permintaan' },
  { text: 'Status Penggunaan Alat', icon: <ConstructionIcon />, path: 'alat-siap-digunakan' },

  // Dosen Specific
  { text: 'Persetujuan Pengajuan Mahasiswa', icon: <ApprovalIcon />, path: 'persetujuan-pengajuan' },
  { text: 'Riwayat Pengajuan Mahasiswa', icon: <AssessmentIcon />, path: 'riwayat-pengajuan' },

  // Common
  { text: 'Scan Alat', icon: <QrCodeScannerIcon />, path: '/scan-alat' },
  { text: 'Profil', icon: <AccountCircleIcon />, path: 'edit-profil' },
];

const laboranMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Persetujuan Alat', icon: <ApprovalIcon />, path: 'persetujuan-alat-laboran' },
  { text: 'Persetujuan Bahan', icon: <ApprovalIcon />, path: 'persetujuan-bahan-laboran' },
  { text: 'Monitoring Alat', icon: <MonitorHeartIcon />, path: 'monitoring-alat' },
  { text: 'Monitoring Bahan', icon: <MonitorHeartIcon />, path: 'monitoring-bahan' },
  { text: 'Jadwal Penggunaan', icon: <CalendarTodayIcon />, path: 'jadwal-penggunaan-laboran' }, // Maybe specific view?
  { text: 'Logbook', icon: <EditNoteIcon />, path: 'logbook-laboran' }, // Maybe specific view?
  { text: 'Scan Alat', icon: <QrCodeScannerIcon />, path: '/scan-alat' },
  // { text: 'Profil', icon: <AccountCircleIcon />, path: 'edit-profil-laboran' },
];

const adminMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Manajemen Pengguna', icon: <PeopleIcon />, path: 'manajemen-pengguna' },
  { text: 'Persetujuan Pengguna', icon: <ApprovalIcon />, path: 'approve-users' },
  { text: 'Manajemen Instrumen', icon: <InventoryIcon />, path: 'manajemen-instrumen' },
  { text: 'Manajemen Alat Gelas', icon: <ScienceIcon />, path: 'manajemen-alat-gelas' },
  { text: 'Manajemen Bahan', icon: <ScienceIcon />, path: 'manajemen-bahan' },
  { text: 'Persetujuan Alat', icon: <ApprovalIcon />, path: 'persetujuan-alat-admin' },
  { text: 'Persetujuan Bahan', icon: <ApprovalIcon />, path: 'persetujuan-bahan-admin' },
  { text: 'Jadwal Penggunaan', icon: <CalendarTodayIcon />, path: 'jadwal-penggunaan' }, // Added for admin
  { text: 'Laporan Logbook', icon: <AssessmentIcon />, path: 'laporan-logbook' },
  { text: 'Laporan Penggunaan Alat', icon: <AssessmentIcon />, path: '/dashboard/laporan-penggunaan-alat' }, // Use absolute path
  { text: 'Laporan Penggunaan Bahan', icon: <AssessmentIcon />, path: 'laporan-penggunaan-bahan' },
  { text: 'Laporan Kerusakan', icon: <ReportProblemIcon />, path: 'laporan-kerusakan' },
  { text: 'Isi Logbook (Admin)', icon: <EditNoteIcon />, path: 'admin-isi-logbook' }, // If admin can fill logs
  { text: 'Scan Alat', icon: <QrCodeScannerIcon />, path: '/scan-alat' },
  // { text: 'Profil', icon: <AccountCircleIcon />, path: 'edit-profil-admin' },
];


const Dashboard = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch User Data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setError('');
        setLoading(true); // Set loading true while fetching user doc
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const fetchedUserData = { uid: user.uid, ...userSnap.data() };
            // Log the fetched role for debugging
            console.log("Fetched user data from Firestore:", fetchedUserData);
            const userRole = fetchedUserData.role;
            console.log("User role:", userRole);

             // Ensure role exists, default if necessary
            if (!userRole) {
                console.error("User role is missing in Firestore document!");
                setError('Peran pengguna tidak ditemukan di database.');
                setUserData(null);
                // Optionally log out user if role is critical and missing
                // await signOut(auth);
                // navigate('/login');
            } else {
                 setUserData(fetchedUserData);
            }
          } else {
            setError('Data pengguna tidak ditemukan di database.');
            setUserData(null);
            await signOut(auth); // Log out if user doc missing
            navigate('/login');
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
          setError('Gagal memuat data pengguna.');
          setUserData(null);
        } finally {
          setLoading(false);
        }
      } else {
        setUserData(null);
        setLoading(false);
        // No need to navigate here, ProtectedRoute should handle it
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login'); // Redirect to login after logout
    } catch (error) {
      console.error("Error signing out: ", error);
      setError("Gagal logout.");
    }
  };

  // --- Select Menu Items Based on Role ---
  const getMenuItems = () => {
    if (!userData) {
        console.log("getMenuItems called but userData is null");
        return [];
    }
    console.log("getMenuItems called with role:", userData.role); // Log the role being checked
    switch (userData.role) {
      case 'admin':
        console.log("getMenuItems: Matched 'admin'");
        return adminMenuItems;
      case 'laboran':
        console.log("getMenuItems: Matched 'laboran'");
        return laboranMenuItems;
      case 'dosen':
        console.log("getMenuItems: Matched 'dosen'");
        return dosenMenuItems;
      case 'mahasiswa':
        console.log("getMenuItems: Matched 'mahasiswa'");
        return mahasiswaMenuItems;
      default:
        console.warn("getMenuItems: Role did not match any case:", userData.role);
        return []; // Or a default minimal menu
    }
  };

  // Ensure userData is available before getting menu items
  const menuItems = userData ? getMenuItems() : [];
  console.log("Final menuItems count:", menuItems.length); // Log how many items are selected

  const drawer = (
    <div>
      <Toolbar /> {/* Spacer for AppBar */}
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            // Use Link component for internal navigation for better SPA behavior
            component={Link}
            to={item.path} // Use the defined path
            onClick={() => mobileOpen && handleDrawerToggle()} // Close drawer on mobile click
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
       {/* Logout Button at the bottom */}
       <List>
         <ListItemButton onClick={handleLogout}>
           <ListItemIcon><LogoutIcon /></ListItemIcon>
           <ListItemText primary="Logout" />
         </ListItemButton>
       </List>
    </div>
  );

  // --- Render Logic ---

  if (loading) {
    return <div>Memuat Dashboard...</div>; // Or a spinner component
  }

  if (error) {
    // Display error prominently, maybe with a retry or logout option
    return <div>Error: {error} <button onClick={handleLogout}>Logout</button></div>;
  }

  if (!userData) {
    // Should be handled by ProtectedRoute, but as a fallback
    return <div>Anda tidak diautentikasi. Mengarahkan ke login...</div>;
  }

  // Capitalize role for display
  const displayRole = userData.role ? userData.role.charAt(0).toUpperCase() + userData.role.slice(1) : 'Pengguna';

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* Header */}
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }} // Only show on mobile
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {displayRole} Dashboard
          </Typography>
          <Tooltip title="Logout">
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="menu"
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        {/* Check for active usage for mahasiswa - show stuck screen if using */}
        <Box sx={{ flexGrow: 1 }}>
          {userData?.role === 'mahasiswa' ? (
            <ActiveUsageScreen>
              <RoleBasedDashboardHome userRole={userData?.role}>
                <Outlet />
              </RoleBasedDashboardHome>
            </ActiveUsageScreen>
          ) : (
            <RoleBasedDashboardHome userRole={userData?.role}>
        <Outlet />
            </RoleBasedDashboardHome>
          )}
        </Box>
        
        {/* Footer with Credit */}
        <Box
          component="footer"
          sx={{
            mt: 'auto',
            py: 2,
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Dikembangkan oleh{' '}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontWeight: 'bold', color: 'primary.main' }}
            >
              Angga Saputra Yasir
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
